import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, FileDown, Pill, Beaker, Sparkles, Wine, Droplet } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { adminAssessmentDetail, adminUpdateFollowup } from "@/lib/adminApi";
import { buildLiverReport } from "@/lib/pdfReport";

const BAND_HEX = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };

/** Admin-side deep-dive dialog for a single assessment.
 *  Shows demographics, Q&A, risk snapshot, recovery plan and lets the
 *  operator regenerate the same PDF the patient received. */
export default function AssessmentDetailDialog({ id, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open || !id) return;
    let alive = true;
    setLoading(true);
    setData(null);
    adminAssessmentDetail(id)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setOutcome(d.followup_outcome || "");
        setNextDate(d.followup_next_date || "");
        setNotes(d.followup_notes || "");
      })
      .catch(() => toast.error("Could not load assessment details"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, open]);

  const saveFollowup = async () => {
    if (!id) return;
    setSavingFollowup(true);
    try {
      const r = await adminUpdateFollowup(id, {
        outcome: outcome || null,
        next_date: nextDate || null,
        notes: notes || null,
      });
      setData((d) => ({ ...d, ...r }));
      toast.success("Follow-up saved");
      onSaved && onSaved();
    } catch {
      toast.error("Could not save follow-up");
    } finally {
      setSavingFollowup(false);
    }
  };

  const downloadPdf = () => {
    if (!data) return;
    try {
      const contact = {
        firstName: (data.name || "").split(" ")[0] || "",
        lastName: (data.name || "").split(" ").slice(1).join(" ") || "",
        phone: data.phone || "",
        email: data.email || "",
        gender: data.gender || "",
        ageYears: data.age_years || null,
        bmiValue: data.bmi_value || null,
        heightCm: data.height_cm || null,
        weightKg: data.weight_kg || null,
      };
      const { doc, shortId } = buildLiverReport(data, contact, data.responses || {});
      const filename = `superage-liver-report-${(data.name || "patient").replace(/\s+/g, "-")}-${shortId || Date.now()}.pdf`;
      doc.save(filename);
    } catch (e) {
      toast.error("PDF generation failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 bg-sa-bg" data-testid="assessment-detail-dialog">
        {loading || !data ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-sa-copper" />
          </div>
        ) : (
          <div className="space-y-6 p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
                  Assessment · {new Date(data.created_at).toLocaleString()}
                </p>
                <h2 className="mt-1 font-display text-2xl font-medium text-sa-forest sm:text-3xl">
                  {data.name || "Anonymous"}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-white"
                    style={{ backgroundColor: BAND_HEX[data.band] || "#5B6660" }}
                  >
                    {data.label} · {data.score}
                  </span>
                  {data.glp1_flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sa-copper/15 px-2 py-0.5 text-[11px] font-semibold text-sa-copper">
                      <Pill className="h-3 w-3" /> GLP-1 candidate
                    </span>
                  )}
                  {data.saroglitazar_flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sa-forest/10 px-2 py-0.5 text-[11px] font-semibold text-sa-forest">
                      <Beaker className="h-3 w-3" /> Saroglitazar candidate
                    </span>
                  )}
                  {data.alcohol_flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                      <Wine className="h-3 w-3" /> Regular drinker
                    </span>
                  )}
                  {data.masld_flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      <Droplet className="h-3 w-3" /> MASLD (fatty liver)
                    </span>
                  )}
                  {(data.red_flag_labels || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                      Red flag · {data.red_flag_labels.length}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="close-detail"
                className="flex h-8 w-8 items-center justify-center rounded-full text-sa-muted transition-colors hover:bg-sa-beige hover:text-sa-forest"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Demographics */}
            <Section title="Demographics">
              <Grid>
                <Field label="Age" value={data.age_years ? `${data.age_years} yrs` : "—"} />
                <Field label="Gender" value={data.gender || "—"} />
                <Field label="Phone" value={data.phone || "—"} />
                <Field label="Email" value={data.email || "—"} />
                <Field label="State" value={data.state || "—"} />
                <Field label="City" value={data.city || "—"} />
                <Field label="BMI" value={data.bmi_value ? data.bmi_value.toFixed(1) : "—"} />
                <Field label="Follow-up" value={data.follow_up?.label || "—"} />
              </Grid>
            </Section>

            {/* Q & A */}
            <Section title="Questionnaire responses">
              <div className="space-y-2.5">
                {(data.qna || []).map((q) => (
                  <div
                    key={q.id}
                    className="flex items-start justify-between gap-4 rounded-2xl border border-sa-beige bg-white px-4 py-3"
                  >
                    <span className="text-[14px] text-sa-forest">{q.question}</span>
                    <span className="text-[13px] font-medium text-sa-forest text-right">{q.answer}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Risk contributors + protective */}
            {(data.top_contributors?.length > 0 || data.top_protective?.length > 0) && (
              <Section title="Risk snapshot">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {data.top_contributors?.length > 0 && (
                    <div className="rounded-2xl border border-sa-beige bg-white p-4">
                      <p className="text-[12px] font-semibold uppercase tracking-widest text-red-500">
                        Contributors
                      </p>
                      <ul className="mt-2 space-y-1.5 text-[13px] text-sa-forest">
                        {data.top_contributors.map((c, i) => (
                          <li key={i}>
                            <span className="font-medium">{c.question}</span>
                            <span className="text-sa-muted"> · {c.answer}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.top_protective?.length > 0 && (
                    <div className="rounded-2xl border border-sa-beige bg-white p-4">
                      <p className="text-[12px] font-semibold uppercase tracking-widest text-green-600">
                        Protective factors
                      </p>
                      <ul className="mt-2 space-y-1.5 text-[13px] text-sa-forest">
                        {data.top_protective.map((c, i) => (
                          <li key={i}>{c.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Recovery plan */}
            {data.recovery_plan?.sections?.length > 0 && (
              <Section title="Recovery plan">
                <div className="space-y-3">
                  {data.recovery_plan.sections.map((s) => (
                    <div key={s.key} className="rounded-2xl border border-sa-beige bg-white p-4">
                      <p className="text-[13px] font-semibold text-sa-forest">{s.title}</p>
                      <ul className="mt-2 space-y-1.5">
                        {s.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-[13px] text-sa-forest/85">
                            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-sa-copper" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Follow-up */}
            <Section title="Follow-up · call outcome">
              <div className="space-y-3 rounded-2xl border border-sa-beige bg-white p-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-[13px] text-sa-forest">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Outcome</span>
                    <select
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                      data-testid="followup-outcome"
                      className="w-full rounded-lg border border-sa-beige bg-white px-3 py-2 text-sa-forest focus:border-sa-forest focus:outline-none"
                    >
                      <option value="">— Select —</option>
                      <option value="not_contacted">Not contacted yet</option>
                      <option value="attempted">Attempted — no answer</option>
                      <option value="called">Spoke with patient</option>
                      <option value="scheduled">Assessment scheduled</option>
                      <option value="booked">Booked & paid</option>
                      <option value="declined">Declined</option>
                      <option value="closed">Closed</option>
                    </select>
                  </label>
                  <label className="text-[13px] text-sa-forest">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Next touchpoint</span>
                    <input
                      type="date"
                      value={nextDate}
                      onChange={(e) => setNextDate(e.target.value)}
                      data-testid="followup-next-date"
                      className="w-full rounded-lg border border-sa-beige bg-white px-3 py-2 text-sa-forest focus:border-sa-forest focus:outline-none"
                    />
                  </label>
                </div>
                <label className="block text-[13px] text-sa-forest">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Notes</span>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="followup-notes"
                    placeholder="Call summary, next steps, concerns raised…"
                    className="w-full resize-y rounded-lg border border-sa-beige bg-white px-3 py-2 text-sa-forest focus:border-sa-forest focus:outline-none"
                  />
                </label>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-sa-muted">
                    {data.followup_updated_at
                      ? `Last updated ${new Date(data.followup_updated_at).toLocaleString()}${data.followup_updated_by ? ` · ${data.followup_updated_by}` : ""}`
                      : "No follow-up recorded yet."}
                  </p>
                  <button
                    onClick={saveFollowup}
                    disabled={savingFollowup}
                    data-testid="save-followup"
                    className="inline-flex items-center gap-2 rounded-full bg-sa-copper px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sa-copper/90 disabled:opacity-60"
                  >
                    {savingFollowup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Save follow-up
                  </button>
                </div>
              </div>
            </Section>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-sa-beige pt-4">
              <button
                onClick={downloadPdf}
                data-testid="download-detail-pdf"
                className="inline-flex items-center gap-2 rounded-full bg-sa-forest px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-sa-forest/90 hover:shadow-lg"
              >
                <FileDown className="h-4 w-4" /> Regenerate PDF
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div>
    <p className="mb-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-sa-copper">
      {title}
    </p>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{children}</div>
);

const Field = ({ label, value }) => (
  <div className="rounded-2xl border border-sa-beige bg-white px-3 py-2.5">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">{label}</p>
    <p className="mt-1 truncate text-sm font-medium text-sa-forest">{value}</p>
  </div>
);
